import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core'
import {
  BulkTransactionModalMode,
  BulkTransactionsModalService,
} from '@helios/create-transactions-modal'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import { BlankSessionService } from '@helios/sessions'
import {
  RootState,
  appendCommands,
  selectActiveSession,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { ComponentContainer } from 'golden-layout'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { InvalidTransactionIdPopoverService } from 'libs/generic-components/src/lib/invalid-transaction-id-popover/invalid-transaction-id-popover.service'
import { Subject, Subscription } from 'rxjs'
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators'
import { TxToUpdateStatus } from './components/bulk-clearwater-transactions-grid/bulk-clearwater-transactions-grid.component'
import {
  BULK_CW_TRAN_LIMIT,
  BulkClearwaterTransactionsGetRequest,
  FindBulkClearwaterTransactionsForm,
  UpdateBulkClearwaterTransactionsForm,
} from './models/bulk-clearwater-transactions.model'
import {
  BulkClearwaterTransaction,
  BulkCwTranUpdateActionType,
  getUniqueRowId,
} from './models/clearwater-transaction.model'
import { BulkClearwaterTransactionsService } from './services/bulk-clearwater-transactions.service'
import { createBulkCwTransGetRequest } from './utils/bulk-clearwater-transactions-util'
import { mapBulkClearwaterTransactionsToBulkTransactions } from './utils/bulk-transaction-wrapper-util'

@Component({
  selector: 'hs-bulk-clearwater-transactions',
  templateUrl: './bulk-clearwater-transactions-panel.component.html',
  styleUrls: ['./bulk-clearwater-transactions-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkClearwaterTransactionsPanelComponent
  extends BasePanelComponent
  implements OnInit, OnDestroy
{
  isSearchPopoverShown = false
  isLoading = false
  maxTransactions = BULK_CW_TRAN_LIMIT
  data: BulkClearwaterTransaction[] = []
  transactionsLimitExceeded = false
  focusedTranIds?: number[] = []

  updateStatuses$: Subject<TxToUpdateStatus> = new Subject()
  subscriptions: Subscription[] = []

  private readonly destroySubject$ = new Subject<void>()
  /** @internal */
  priorRequest?: BulkClearwaterTransactionsGetRequest

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly bulkService: BulkClearwaterTransactionsService,
    private readonly bulkTransactionsModalService: BulkTransactionsModalService,
    private readonly blankSessionService: BlankSessionService,
    private readonly invalidTransactionIdPopoverService: InvalidTransactionIdPopoverService,
    private readonly store: Store<RootState>,
    public toastService: ToastService,
  ) {
    super(container)
  }

  ngOnInit() {
    this.store
      .select(selectActiveSession)
      .pipe(
        takeUntil(this.destroySubject$),
        distinctUntilChanged((a, b) => a?.id === b?.id),
      )
      .subscribe(() => {
        if (this.data && this.data.some(d => d.isEdited)) this.refreshData()
      })
  }

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  async onDownloadSubmit(form: FindBulkClearwaterTransactionsForm) {
    this.isSearchPopoverShown = false
    this.isLoading = true
    this.updateStatuses$.next()
    const request = createBulkCwTransGetRequest(form)
    this.subscriptions.push(this.getData(request))
  }

  async onFind(form: FindBulkClearwaterTransactionsForm) {
    this.isLoading = true
    this.updateStatuses$.next()
    const request = createBulkCwTransGetRequest(form)
    this.subscriptions.push(this.getData(request))
  }

  onCancelRequest() {
    this.subscriptions.forEach(s => s.unsubscribe())
    this.isLoading = false
    this.data = []
    this.isSearchPopoverShown = false
  }

  getData(request: BulkClearwaterTransactionsGetRequest) {
    const tranIds = request.filter.tranIds
    const legacyTranIds = request.filter.legacyTranIds

    this.maxTransactions = BULK_CW_TRAN_LIMIT
    return this.bulkService
      .getBulkClearwaterTransactions(request)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe(
        async response => {
          this.isLoading = false

          if (response == null) return
          let missingIds: string[] | number[] = []
          if (tranIds) {
            const responseIds = response.data.map(
              resData => resData.transactionId,
            )
            missingIds = tranIds.filter(tranId => !responseIds.includes(tranId))
          }
          if (legacyTranIds) {
            const responseOrigIds = response.data.map(
              resData => resData.originalId,
            )
            const responseLegacyIds = response.data.map(resData => resData.id)
            missingIds = legacyTranIds.filter(
              legTranId =>
                !(
                  responseOrigIds.includes(legTranId) ||
                  responseLegacyIds.includes(legTranId)
                ),
            )
          }
          if (missingIds.length > 0) {
            try {
              await this.invalidTransactionIdPopoverService.toggle(missingIds)
            } catch (error) {
              return
            }
          }
          this.setMatchingTranCount(response.matchingTransCount)
          this.data = response.data
          this.priorRequest = request
        },
        error => {
          this.toastService.error(
            `Error getting bulk clearwater transactions: ${error.error.errorId}`,
          )
          this.isLoading = false
        },
      )
  }

  clearTable() {
    this.data = []
  }

  refreshData() {
    if (this.priorRequest == null) return
    this.subscriptions.push(this.getData(this.priorRequest))
  }

  async bulkEditTx(transactions: BulkClearwaterTransaction[]) {
    const isTwoSidedTrno = Boolean(
      transactions[0].destAccountId && transactions[0].destAccountId != -1,
    )
    this.bulkTransactionsModalService.toggleBulkTransactionModal(
      transactions[0].type,
      {
        modalMode: BulkTransactionModalMode.EditCWTran,
        transactions:
          mapBulkClearwaterTransactionsToBulkTransactions(transactions),
        isTwoSidedTrno: isTwoSidedTrno,
      },
    )
  }

  async bulkCopyTx(transactions: BulkClearwaterTransaction[]) {
    const isTwoSidedTrno = Boolean(
      transactions[0].destAccountId && transactions[0].destAccountId != -1,
    )
    this.bulkTransactionsModalService.toggleBulkTransactionModal(
      transactions[0].type,
      {
        modalMode: BulkTransactionModalMode.CopyCWTran,
        transactions:
          mapBulkClearwaterTransactionsToBulkTransactions(transactions),
        isTwoSidedTrno: isTwoSidedTrno,
      },
    )
  }

  async updateBulkAction(
    transactions: BulkClearwaterTransaction[],
    actionType: BulkCwTranUpdateActionType,
  ) {
    const uniqueTranIds = transactions.map(t => getUniqueRowId(t))
    this.updateStatuses$.next({
      uniqueTranIds: uniqueTranIds,
      requestStatus: 'LOADING',
    })
    await this.blankSessionService.createBlankSessionIfNotInSession()

    const updateBulkForm: UpdateBulkClearwaterTransactionsForm = {
      transactions: transactions.map(t => ({
        transactionId: t.transactionId,
        accountId: t.accountId,
      })),
      type: actionType,
    }
    try {
      const response = await this.bulkService
        .updateBulkClearwaterTransactions(updateBulkForm)
        .toPromise()

      this.updateStatuses$.next({
        uniqueTranIds: uniqueTranIds,
        requestStatus: 'SUCCESS',
      })

      this.store.dispatch(appendCommands({ commands: response.commands }))
    } catch {
      this.updateStatuses$.next({
        uniqueTranIds: uniqueTranIds,
        requestStatus: 'FAILURE',
      })
    }
  }

  setMatchingTranCount(matchingTranCount: string) {
    this.transactionsLimitExceeded = matchingTranCount.endsWith('+')
  }

  clearAction(transactions: BulkClearwaterTransaction[]) {
    this.updateBulkAction(transactions, BulkCwTranUpdateActionType.CLEAR)
  }

  clearAndSetPostDateAction(transactions: BulkClearwaterTransaction[]) {
    this.updateBulkAction(
      transactions,
      BulkCwTranUpdateActionType.CLEAR_AND_UPDATE_POST,
    )
  }

  unclearAction(transactions: BulkClearwaterTransaction[]) {
    this.updateBulkAction(transactions, BulkCwTranUpdateActionType.UNCLEAR)
  }

  deleteAction(transactions: BulkClearwaterTransaction[]) {
    this.updateBulkAction(transactions, BulkCwTranUpdateActionType.DELETE)
  }

  deleteAndIgnoreAction(transactions: BulkClearwaterTransaction[]) {
    this.updateBulkAction(
      transactions,
      BulkCwTranUpdateActionType.DELETE_AND_IGNORE,
    )
  }
  deleteAndHoldAction(transaction: BulkClearwaterTransaction[]) {
    this.updateBulkAction(
      transaction,
      BulkCwTranUpdateActionType.DELETE_AND_HOLD,
    )
  }
}
